import React from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';
import colors from '../../theme/colors';
import { Meetup } from '../../services/firebaseService';

const defaultEventImg = require('../../assets/default-event.jpg');

interface EventCardProps {
  meetup: Meetup;
  onReadMore: (meetup: Meetup) => void;
  showReadMore?: boolean;
}

const eventCardSx = {
  p: 0,
  height: '100%',
  textAlign: 'center',
  borderRadius: 3,
  background: colors.paper,
  boxShadow: 2,
  // border: `1.5px solid ${colors.green}`,
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-6px) scale(1.02)',
    boxShadow: 5,
  },
};

const EventCard: React.FC<EventCardProps> = ({ meetup, onReadMore, showReadMore = true }) => {
  const truncateDescription = (text: string, maxLines: number = 3) => {
    const words = text.split(' ');
    const maxWords = maxLines * 8; // Approximate words per line
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + '...';
  };

  return (
    <Paper elevation={3} sx={eventCardSx}>
      <Box sx={{ width: '100%', height: 180, borderTopLeftRadius: 12, borderTopRightRadius: 12, overflow: 'hidden', background: colors.lightGray }}>
        <img
          src={meetup.imageUrl || defaultEventImg}
          alt={meetup.title}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          onError={e => { (e.target as HTMLImageElement).src = defaultEventImg; }}
        />
      </Box>
      <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={600} gutterBottom color={colors.textPrimary}>
          {meetup.title}
        </Typography>
        <Typography variant="body2" color={colors.textSecondary} sx={{ mb: 1 }}>
          {new Date(meetup.date).toLocaleDateString()}
        </Typography>
        <Typography variant="body2" color={colors.green} sx={{ mb: 1, fontWeight: 600, fontSize: '0.9rem' }}>
          {meetup.speakerName} • {meetup.speakerRole}
        </Typography>
        {meetup.location && (
          <Typography variant="body2" color={colors.textSecondary} sx={{ mb: 1 }}>
            📍 {meetup.location}
          </Typography>
        )}
        {meetup.numberOfAttendees && (
          <Typography variant="body2" color={colors.textSecondary} sx={{ mb: 1 }}>
            👥 {meetup.numberOfAttendees} attendees
          </Typography>
        )}
        <Typography variant="body2" color={colors.textSecondary} sx={{ mb: 2, lineHeight: 1.4 }}>
          {truncateDescription(meetup.description)}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
          {showReadMore && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => onReadMore(meetup)}
              sx={{ fontWeight: 600 }}
            >
              Read More
            </Button>
          )}
          {meetup.link && (
            <Button
              href={meetup.link}
              target="_blank"
              rel="noopener"
              variant="outlined"
              color="primary"
              size="small"
              sx={{ fontWeight: 600 }}
            >
              Watch Now
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default EventCard;
